import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Grid,
  GridItem,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import parse from "html-react-parser";
import { Link as RouterLink, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import WorkbookEmptyState from "./WorkbookEmptyState";
import WorkbookProgressChecklist from "./WorkbookProgressChecklist";
import WorkbookStepNavigation from "./WorkbookStepNavigation";
import useWorkbookChecklistProgress from "./useWorkbookChecklistProgress";
import {
  getWorkbookPageInterpretation,
  resolveWorkbookOrganizationSurveyId,
} from "../organizationWorkbookApi";
import { getWorkbookRoutePrefix, isReadOnlyWorkbookRoute, workbookPath } from "./workbookRouting";
import { formatOrganizationSurveyTitle } from "../../../utils/organizationSurveyHelper";

const pickChecklistItems = (interpretationPayload) =>
  interpretationPayload?.diagnostic_checklist ||
  interpretationPayload?.checklist ||
  [];

const pickInterpretation = (payload) =>
  payload?.interpretation || {
    id: payload?.interpretation_id,
    description: payload?.description,
    diagnostic: payload?.diagnostic,
    cornerstones: payload?.cornerstones,
    diagnostic_checklist: payload?.diagnostic_checklist,
    checklist: payload?.checklist,
  };

const getInterpretationId = (payload, interpretation, routeStateId) =>
  routeStateId || payload?.interpretation_id || interpretation?.id || null;

const pickDiagnosticContent = (interpretation) =>
  interpretation?.diagnostic || interpretation?.cornerstones || "";

const InterpretationContentCard = ({ title, children, emptyMessage }) => (
  <Card borderRadius="xl" borderWidth="1px" borderColor="gray.200" bg="white" boxShadow="sm">
    <CardHeader
      px={{ base: 5, md: 7 }}
      py={4}
      borderBottomWidth="1px"
      borderColor="gray.200"
    >
      <Text
        fontSize="xs"
        color="gray.500"
        fontWeight="bold"
        textTransform="uppercase"
        letterSpacing="0.08em"
      >
        {title}
      </Text>
    </CardHeader>
    <CardBody px={{ base: 5, md: 7 }} py={{ base: 5, md: 7 }}>
      {children ? (
        <Box className="tiptap-content" color="gray.700" lineHeight="1.8">
          {children}
        </Box>
      ) : (
        <Text color="gray.600">{emptyMessage}</Text>
      )}
    </CardBody>
  </Card>
);

const WorkbookInterpretationWorkspace = () => {
  const { t } = useTranslation();
  const { pageId, organizationId } = useParams();
  const location = useLocation();
  const routePrefix = getWorkbookRoutePrefix(location.pathname);
  const readOnly = isReadOnlyWorkbookRoute(location.pathname, location.state);
  const interpretationIdFromState = location.state?.interpretationId || null;
  const organizationSurveyIdFromState =
    location.state?.organizationSurveyId || null;

  const [loading, setLoading] = useState(true);
  const [organizationSurveyId, setOrganizationSurveyId] = useState(null);
  const [interpretationPayload, setInterpretationPayload] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    const loadInterpretation = async () => {
      try {
        setLoading(true);
        setError("");

        const resolvedSurveyId = await resolveWorkbookOrganizationSurveyId({
          routeStateOrganizationSurveyId: organizationSurveyIdFromState,
          organizationId,
        });
        const nextInterpretationPayload = await getWorkbookPageInterpretation(
          resolvedSurveyId,
          pageId
        );

        if (!active) return;
        setOrganizationSurveyId(resolvedSurveyId);
        setInterpretationPayload(nextInterpretationPayload);
      } catch (err) {
        if (!active) return;
        setError("Could not load the workbook.");
      } finally {
        if (active) setLoading(false);
      }
    };

    loadInterpretation();

    return () => {
      active = false;
    };
  }, [organizationId, organizationSurveyIdFromState, pageId]);

  const interpretation = useMemo(
    () => pickInterpretation(interpretationPayload),
    [interpretationPayload]
  );
  const hasInterpretationContent = Boolean(
    interpretation?.description || pickDiagnosticContent(interpretation)
  );
  const diagnosticContent = pickDiagnosticContent(interpretation);
  const interpretationId = getInterpretationId(
    interpretationPayload,
    interpretation,
    interpretationIdFromState
  );
  const fallbackChecklistItems = useMemo(
    () => pickChecklistItems(interpretation),
    [interpretation]
  );

  const checklist = useWorkbookChecklistProgress({
    interpretationId,
    organizationSurveyId,
    fallbackItems: fallbackChecklistItems,
    readOnly,
  });

  const widgetsRouteState = {
    ...location.state,
    organizationSurveyId,
    pageId,
    ...(readOnly ? { readOnly: true } : {}),
    ...(interpretationId ? { interpretationId } : {}),
  };
  const surveyTitle = formatOrganizationSurveyTitle(interpretationPayload || {});

  if (loading) {
    return (
      <Box py={10} textAlign="center">
        <Spinner size="xl" />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  if (!hasInterpretationContent && checklist.items.length === 0) {
    return (
      <WorkbookEmptyState
        title={t("organization.workbook.no_interpretation_title", "Interpretation coming soon")}
        description={t(
          "organization.workbook.no_interpretation_description",
          "This page does not currently have interpretation content available."
        )}
      />
    );
  }

  return (
    <VStack align="stretch" spacing={6}>
      <WorkbookStepNavigation
        pageId={pageId}
        organizationSurveyId={organizationSurveyId}
        interpretationId={interpretationId}
        currentStep="interpretation"
        routePrefix={routePrefix}
        readOnly={readOnly}
      />

      {surveyTitle ? (
        <Text fontWeight="semibold">{surveyTitle}</Text>
      ) : null}

      {checklist.error ? (
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          {checklist.error}
        </Alert>
      ) : null}

      <Grid
        templateColumns={{ base: "1fr", lg: "minmax(0, 1fr) minmax(320px, 420px)" }}
        gap={{ base: 5, lg: 8 }}
        alignItems="start"
      >
        <GridItem>
          <VStack align="stretch" spacing={5}>
            <InterpretationContentCard
              title={t("organization.workbook.interpretation_title", "Interpretation")}
              emptyMessage={t(
                "organization.workbook.no_interpretation_description_body",
                "No interpretation description is available yet."
              )}
            >
              {interpretation?.description ? parse(interpretation.description) : null}
            </InterpretationContentCard>

            <InterpretationContentCard
              title={t("organization.workbook.diagnostic_title", "Diagnostic")}
              emptyMessage={t(
                "organization.workbook.no_diagnostic_body",
                "No diagnostic content is available yet."
              )}
            >
              {diagnosticContent ? parse(diagnosticContent) : null}
            </InterpretationContentCard>
          </VStack>
        </GridItem>

        <GridItem>
          <WorkbookProgressChecklist
            title={t(
              "organization.workbook.diagnostic_checklist_title",
              "Diagnostic Checklist"
            )}
            items={checklist.items}
            progress={checklist.progress}
            loading={checklist.loading}
            completedCount={checklist.completedCount}
            totalCount={checklist.totalCount}
            percentComplete={checklist.percentComplete}
            onSelect={() => {}}
            onToggle={checklist.toggleItem}
            readOnly={readOnly}
            footer={
              <Box pt={1}>
                <Button
                  as={RouterLink}
                  to={workbookPath(routePrefix, `pages/${pageId}/widgets`)}
                  state={widgetsRouteState}
                  colorScheme="blue"
                  w="100%"
                  py={6}
                  borderRadius="lg"
                  isDisabled={!organizationSurveyId}
                >
                  {t(
                    "organization.workbook.see_next_day_practices",
                    "See My Next Day Practices →"
                  )}
                </Button>
              </Box>
            }
          />
        </GridItem>
      </Grid>
    </VStack>
  );
};

export default WorkbookInterpretationWorkspace;
